/* eslint-disable react/require-default-props */
/* eslint-disable react/jsx-filename-extension */
import { AccessControl } from '@nile/components';
import TicketCount from '@nile/mangifera/src/Components/Ticket/TicketCount';
import {
  Button,
  DisplayText,
  Link,
  SkeletonBodyText,
  TextStyle
} from '@superops/ui-design-system';
import React, { Children, PropsWithChildren, ReactNode } from 'react';
import { NavLink } from 'react-router-dom';
// eslint-disable-next-line import/extensions
import { ManageSheetProps, ManageLeftPane } from './ManageLeftPaneSheet';
import { SVGIcon } from '@superops/super-icons';

type LeftNavPaneProps = PropsWithChildren<{
  sheetProps: ManageSheetProps;
  isLoading: boolean;
}>;

type SectionHeaderProps = {
  text: React.ReactNode;
  // eslint-disable-next-line react/require-default-props
  button?: React.ReactNode;
};

type LinkItemProps = {
  emoji: React.ReactNode;
  text: React.ReactNode;
  count?: number | string;
  isCountLoading?: boolean;
  onClick: () => void;
  to: string;
};

type SectionProps = PropsWithChildren<{ header: React.ReactNode }>;

interface CreateButtonProps {
  url: string;
  roleToCheck: string;
  onClick: () => void;
}

interface LeftNavPaneComposition {
  SectionHeader?: (props: SectionHeaderProps) => ReactNode;
  LinkItem?: (props: PropsWithChildren<LinkItemProps>) => ReactNode;
  Section?: (props: PropsWithChildren<SectionProps>) => ReactNode;
  CreateButton?: (props: CreateButtonProps) => ReactNode;
}

const LeftNavPane: React.FC<LeftNavPaneProps> &
  LeftNavPaneComposition = function LeftNavPane({
  children,
  sheetProps,
  isLoading
}: LeftNavPaneProps) {
  if (isLoading) return <LeftPaneSkeletonLoader />;
  return (
    <ManageLeftPane>
      <div>{children}</div>
      <ManageLeftPane.OpenSheetButton />
      <ManageLeftPane.Sheet {...sheetProps} />
    </ManageLeftPane>
  );
};

LeftNavPane.SectionHeader = function SectionHeader({ text, button }) {
  return (
    <div className="fx pl10 pb10 mb-10 mt-10 divider">
      <div className="flex aend fx2">
        <DisplayText weight="Medium" size="medium">
          <TextStyle variation="subdued" uppercase>
            {text}
          </TextStyle>
        </DisplayText>
      </div>
      <div className="flex">{button}</div>
    </div>
  );
};

LeftNavPane.LinkItem = function LinkItem({
  emoji,
  text,
  count,
  isCountLoading,
  onClick,
  to,
  children
}) {
  if (children) return <>{children}</>;

  return (
    <NavLink
      className="fx actr navLink"
      strict
      replace
      onClick={onClick}
      to={to}
    >
      <>
        {emoji}
        <div className="f11 ellipsis">{text}</div>
        {count != null && (
          <div className="ml-20">
            <TicketCount loading={isCountLoading} count={count} />
          </div>
        )}
      </>
    </NavLink>
  );
};

LeftNavPane.Section = function Section({ header, children }) {
  // const hasContent = Children.count(children) > 0;
  return (
    <>
      {header}
      <div className="mb40">{children}</div>
    </>
  );
};

LeftNavPane.CreateButton = function CreateButton({
  url,
  roleToCheck,
  onClick
}) {
  const button = (
    <div>
      <Button
        icon={<SVGIcon name="Add" />}
        onClick={onClick}
        plain
        monochrome
        iconColor="#0A0B10"
      />
    </div>
  );
  return (
    <AccessControl
      roleToCheck={roleToCheck}
      render={
        url ? (
          <div className="flex">
            <Link removeUnderline url={url}>
              {button}
            </Link>
          </div>
        ) : (
          button
        )
      }
    />
  );
};

export default LeftNavPane;

function LeftPaneSkeletonLoader() {
  return (
    <div className="p20">
      <div className="mb40">
        <SkeletonBodyText lines={3} />
      </div>
      <div className="mb40">
        <SkeletonBodyText lines={3} />
      </div>
      <div className="mb20">
        <SkeletonBodyText lines={3} />
      </div>
    </div>
  );
}
